import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useState,
} from "react";

import * as API from "@ekonsilio/api-livechat";
import axios from "axios";
// eslint-disable-next-line camelcase
import jwt_decode from "jwt-decode";
import PropTypes from "prop-types";
import { useCookies } from "react-cookie";

import parseCookie from "./cookie";
import { useAddHistoryMutation } from "./queries/conversations";
import { useGetIntegrationQuery } from "./queries/integration";
import { getBehaviorsSessionStorage } from "./session-storage";

axios.defaults.baseURL = process.env.REACT_APP_API_LIVECHAT;
API.Settings.BaseURL = process.env.REACT_APP_API_LIVECHAT;

const authenticationApi = new API.Authentication.Auth();
const webmessengerApi = new API.Messaging.Webmessenger();

const WebmessengerContext = createContext();

function WebmessengerProvider({ children }) {
    const [cookies, setCookie, removeCookie] = useCookies([
        "eK-token",
        "eK-global",
        "eK-conversation",
    ]);

    const [integration, setIntegration] = useState(null);
    const [visitorSatisfactionIntegration, setVisitorSatisfactionIntegration] =
        useState(false);
    const [timeSlots, setTimeSlots] = useState(null);
    const [transferAIHuman, setTransferAIHuman] = useState(false);
    const [operator, setOperator] = useState(null);
    const [greeting, setGreeting] = useState(null);
    const [notification, setNotification] = useState(null);
    const [conversation, setConversation] = useState(null);
    const [token, setToken] = useState(null);

    const [hidden, setHidden] = useState(true);
    const [maximized, setMaximized] = useState(false);
    const [typing, setTyping] = useState(false);
    const [online, setOnline] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [messages, setMessages] = useState([]);
    const [unreadMessagesCount, setUnreadMessagesCount] = useState(0);

    const { mutate: addHistory } = useAddHistoryMutation();

    const setGlobalSessionStorage = (obj) => {
        const expires = new Date();
        expires.setHours(expires.getHours() + 6);

        setCookie(
            "eK-global",
            {
                ...cookies["eK-global"],
                ...obj,
            },
            {
                secure: true,
                path: "/",
                sameSite: "strict",
                expires,
            }
        );
    };

    const incrementUnreadMessagesCount = (count = 1) => {
        setUnreadMessagesCount((prevCount) => prevCount + count);
    };

    const resetUnreadMessagesCount = () => setUnreadMessagesCount(0);

    const openWindow = (persist) => {
        setMaximized(true);

        if (persist && !conversation === false)
            setGlobalSessionStorage({ maximized: true });
    };

    const closeWindow = () => {
        setMaximized(false);
        resetUnreadMessagesCount();
        if (conversation) setGlobalSessionStorage({ maximized: false });
    };

    const hide = () => setHidden(true);
    const show = () => setHidden(false);

    const closeNotification = () => {
        setNotification(null);

        const storage = getBehaviorsSessionStorage();

        // Remove behavior notification
        if (
            (storage.triggered?.greeting?.message?.behavior &&
                !storage.triggered?.notification?.[
                    storage.triggered?.greeting?.message?.behavior
                ]) ||
            storage.triggered?.greeting?.message?.behavior
        ) {
            storage.triggered = {
                ...storage.triggered,
                notification: {
                    ...storage.triggered.notification,
                    [storage.triggered.greeting.message.behavior]: true,
                },
            };
        }

        if (notification?.behavior) {
            storage.triggered = {
                ...storage.triggered,
                notification: {
                    ...storage.triggered.notification,
                    [notification.behavior]: true,
                },
            };
        }

        // Remove conversation notification
        if (storage.triggered.notificationConversation)
            storage.triggered = {
                ...storage.triggered,
                notificationConversation: false,
            };

        sessionStorage.setItem("eK-behaviors", JSON.stringify(storage));
    };
    const submitNotification = () => {
        setNotification(null);
        openWindow();
    };
    const createNotification = (data) => {
        setNotification({
            title: data.title,
            text: data.text,
            behavior: data.behavior,
        });
    };

    const login = (authToken) => {
        const expires = new Date(jwt_decode(authToken).exp * 1000);
        expires.setHours(expires.getHours() - 2);

        setToken(authToken);
        API.Settings.Authorization = {
            type: "Bearer",
            token: authToken,
        };
        axios.defaults.headers.common.Authorization = `Bearer ${authToken}`;

        setCookie("eK-token", authToken, {
            secure: true,
            path: "/",
            sameSite: "strict",
            expires,
        });
    };

    const populateState = (conv) => {
        const { participants } = conv;
        const conversationSession = cookies["eK-conversation"];

        setConversation(conv);

        const currentOperator = participants.operators.find((o) => o.current);
        const lastOperator =
            participants.operators[participants.operators.length - 1];

        if (!currentOperator && conversationSession && lastOperator)
            setOperator(lastOperator);
        else setOperator(currentOperator);
    };

    const storageEvent = useCallback(
        (event) => {
            if (event.storageArea !== localStorage) return;
            if (
                event.key === "eK-conversation:new" &&
                event.newValue &&
                event.newValue === integration
            ) {
                const tokenCookie =
                    token || parseCookie(document.cookie)["eK-token"];

                if (tokenCookie && !conversation) {
                    setToken(tokenCookie);
                    API.Settings.Authorization = {
                        type: "Bearer",
                        token: tokenCookie,
                    };

                    axios.defaults.headers.common.Authorization = `Bearer ${tokenCookie}`;

                    const conversationSession =
                        cookies["eK-conversation"]?.conversation;

                    if (conversationSession) {
                        webmessengerApi
                            .getConversation(conversationSession)
                            .then((conv) => {
                                populateState(conv);
                                if (!maximized) setMaximized(true);
                            })
                            .catch(() => {});
                    }
                }
            }
        },
        [integration, conversation, token, maximized]
    );

    useGetIntegrationQuery(integration, {
        onSuccess: ({ data }) => {
            setVisitorSatisfactionIntegration(data?.visitorSatisfaction);
            setTransferAIHuman(data?.settings?.transferAIHuman);
            setTimeSlots(data?.timeSlots);
        },
        enabled: !!integration,
    });

    useEffect(() => {
        window.addEventListener("storage", storageEvent);
        return () => {
            window.removeEventListener("storage", storageEvent);
        };
    }, [storageEvent]);

    useEffect(() => {
        if (
            conversation &&
            conversation?._id &&
            (!cookies["eK-conversation"] ||
                !cookies["eK-conversation"]?.conversation)
        ) {
            const expires = new Date();
            expires.setHours(expires.getHours() + 12);

            setCookie(
                "eK-conversation",
                {
                    conversation: conversation._id.toString(),
                },
                {
                    secure: true,
                    path: "/",
                    sameSite: "strict",
                    expires,
                }
            );
        }
    }, [conversation]);

    useEffect(() => {
        if (window.eK && window.eK.integration && integration === null)
            setIntegration(window.eK.integration);

        if (cookies["eK-token"]) {
            setToken(cookies["eK-token"]);
            API.Settings.Authorization = {
                type: "Bearer",
                token: cookies["eK-token"],
            };

            axios.defaults.headers.common.Authorization = `Bearer ${cookies["eK-token"]}`;

            authenticationApi
                .login({})
                .then((data) => {
                    if (data.token) login(data.token);

                    const conversationSession =
                        cookies["eK-conversation"]?.conversation;

                    if (conversationSession) {
                        webmessengerApi
                            .getConversation(conversationSession)
                            .then((conv) => {
                                if (conv) {
                                    populateState(conv);

                                    addHistory({
                                        _id: conv._id,
                                        url: window.location.href,
                                    });

                                    setIsLoading(false);

                                    setMaximized(
                                        cookies["eK-global"]?.maximized || false
                                    );
                                } else {
                                    if (cookies["eK-global"])
                                        removeCookie("eK-global", {
                                            path: "/",
                                        });

                                    setIsLoading(false);
                                }
                            })
                            .catch(() => {
                                if (cookies["eK-global"])
                                    removeCookie("eK-global", {
                                        path: "/",
                                    });
                                setIsLoading(false);
                            });
                    } else {
                        if (cookies["eK-global"])
                            removeCookie("eK-global", {
                                path: "/",
                            });

                        setIsLoading(false);
                    }
                })
                .catch(() => {
                    setToken(null);
                    API.Settings.Authorization = {
                        type: "Bearer",
                        token: null,
                    };

                    delete axios.defaults.headers.common.Authorization;

                    removeCookie("eK-global", {
                        path: "/",
                    });
                    removeCookie("eK-token", {
                        path: "/",
                    });

                    setIsLoading(false);
                });
        } else setIsLoading(false);
    }, []);

    useEffect(() => {
        if (greeting && !!greeting?.message && !maximized) {
            setUnreadMessagesCount(1);
        }
    }, [greeting?.message]);

    useEffect(() => {
        if (typing && operator?.type === "agent") {
            const timeout = setTimeout(() => {
                setTyping(false);
            }, 15000);

            return () => clearTimeout(timeout);
        }
        return undefined;
    }, [typing, operator]);

    // useEffect(() => {
    //     if (maximized && !document.body.classList.contains("ek-noScroll")) {
    //         document.body.classList.add("ek-noScroll");
    //     } else if (
    //         !maximized &&
    //         document.body.classList.contains("ek-noScroll")
    //     ) {
    //         document.body.classList.remove("ek-noScroll");
    //     }
    // }, [maximized]);

    const handleSetMessages = setMessages;

    const value = React.useMemo(
        () => ({
            integration,
            visitorSatisfactionIntegration,
            transferAIHuman,
            timeSlots,

            hidden,
            hide,
            show,

            maximized,
            openWindow,
            closeWindow,

            operator,
            setOperator,

            greeting,
            setGreeting,

            notification,
            closeNotification,
            submitNotification,
            createNotification,

            conversation,
            setConversation,

            online,
            setOnline,
            token,
            login,

            typing,
            setTyping,

            messages,
            handleSetMessages,
            incrementUnreadMessagesCount,
            resetUnreadMessagesCount,
            unreadMessagesCount,
        }),
        [
            integration,
            visitorSatisfactionIntegration,
            transferAIHuman,
            timeSlots,
            hidden,
            maximized,
            operator,
            greeting,
            notification,
            conversation,
            online,
            token,
            typing,
            messages,
            unreadMessagesCount,
        ]
    );

    return (
        <WebmessengerContext.Provider value={value}>
            {!isLoading && children}
        </WebmessengerContext.Provider>
    );
}

function useWebmessenger() {
    const context = useContext(WebmessengerContext);
    if (context === undefined) {
        throw new Error(
            "useWebmessenger must be used within a WebmessengerProvider"
        );
    }
    return context;
}

WebmessengerProvider.propTypes = {
    children: PropTypes.element.isRequired,
};

export { WebmessengerProvider, useWebmessenger };
